import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '@core/api/api.service';
import { AuthService } from '@core/auth/auth.service';
import { BasketSummary } from '@core/models/models';
import { LocalProfileService } from '@shared/services/local-profile.service';

@Component({
  selector: 'app-baskets-manager',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="baskets-page" dir="rtl">
      <header class="baskets-hero">
        <div class="hero-title-group">
          <h1>הרשימות שלי</h1>
        </div>
        <div class="hero-actions">
          <button
            type="button"
            class="btn-primary create-basket-btn"
            data-testid="create-new-basket-button"
            (click)="createNewBasket()"
            [disabled]="creating"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{{ creating ? 'יוצר רשימה…' : 'רשימה חדשה' }}</span>
          </button>
        </div>
      </header>

      @if (!auth.isLoggedIn) {
        <section class="card guest-warning" data-testid="baskets-guest-prompt">
          <div class="warning-icon-wrap" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div class="warning-content">
            <h2>יש להתחבר כדי לנהל מספר רשימות</h2>
            <p>התחברו באמצעות חשבון Google כדי לשמור רשימות, לשתף אותן בקבוצות ולפתוח רשימות נוספות.</p>
          </div>
        </section>
      } @else {
        @if (loading) {
          <div class="loading-state" role="status">
            <span class="spinner" aria-hidden="true"></span>
            <span>טוען את הרשימות שלך…</span>
          </div>
        } @else if (errorMessage) {
          <p class="error-msg" role="alert">{{ errorMessage }}</p>
        } @else {
          <section class="baskets-section">
            <div class="section-title-wrap">
              <h2>רשימות פתוחות ({{ openBaskets.length }})</h2>
            </div>

            @if (openBaskets.length === 0) {
              <div class="empty-baskets-card">
                <p>אין לך כרגע רשימות פתוחות.</p>
                <button type="button" class="btn-secondary" (click)="createNewBasket()">
                  רשימה חדשה
                </button>
              </div>
            } @else {
              <ul class="list-rows">
                @for (basket of openBaskets; track basket.id) {
                  <li class="list-row" data-testid="basket-item-card">
                    <button
                      type="button"
                      class="list-name"
                      [class.is-open]="isOpenList(basket.id)"
                      [disabled]="actionPendingId === basket.id"
                      (click)="openList(basket)"
                    >
                      <span class="list-name-label">{{ basket.name || 'רשימה ללא שם' }}</span>
                    </button>
                    @if (basket.groupId) {
                      <span class="badge badge-shared" [title]="basket.groupName || 'קבוצה'">משותף</span>
                    }
                    <span class="list-count">{{ basket.itemCount }} פריטים</span>
                    <button
                      type="button"
                      class="list-delete"
                      [disabled]="actionPendingId === basket.id"
                      (click)="deleteBasket(basket)"
                      aria-label="מחק"
                      title="מחק"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path d="M4 7h16"></path>
                        <path d="M9 7V5h6v2"></path>
                        <path d="M8 7l1 12h6l1-12"></path>
                      </svg>
                    </button>
                  </li>
                }
              </ul>
            }
          </section>

          @if (closedBaskets.length > 0) {
            <section class="baskets-section closed-section">
              <div class="section-title-wrap">
                <h2>רשימות סגורות ({{ closedBaskets.length }})</h2>
                <p class="section-hint">לחיצה על השם פותחת את הרשימה מחדש.</p>
              </div>
              <ul class="list-rows">
                @for (basket of closedBaskets; track basket.id) {
                  <li class="list-row" data-testid="closed-basket-item-card">
                    <button
                      type="button"
                      class="list-name"
                      [disabled]="actionPendingId === basket.id"
                      (click)="openList(basket)"
                    >
                      {{ basket.name || 'רשימה ללא שם' }}
                    </button>
                    @if (basket.groupId) {
                      <span class="badge badge-shared" [title]="basket.groupName || 'קבוצה'">משותף</span>
                    }
                    <span class="list-count">{{ basket.itemCount }} פריטים</span>
                    <button
                      type="button"
                      class="list-delete"
                      [disabled]="actionPendingId === basket.id"
                      (click)="deleteBasket(basket)"
                      aria-label="מחק"
                      title="מחק"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path d="M4 7h16"></path>
                        <path d="M9 7V5h6v2"></path>
                        <path d="M8 7l1 12h6l1-12"></path>
                      </svg>
                    </button>
                  </li>
                }
              </ul>
            </section>
          }
        }
      }

      @if (askFirstGroup) {
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="first-group-title" (click)="cancelFirstGroup()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2 id="first-group-title">הקבוצה הראשונה</h2>
              <button type="button" class="modal-close-btn" (click)="cancelFirstGroup()" aria-label="סגירה">×</button>
            </header>
            <div class="modal-body">
              <p>אין עדיין קבוצה. הרשימה החדשה לא תיצור קבוצה לבד. איך לקרוא לקבוצה הראשונה?</p>
              <input
                type="text"
                class="first-group-input"
                [(ngModel)]="firstGroupName"
                name="firstGroupName"
                placeholder="לדוגמה: הבית"
                data-testid="first-group-name-input"
              />
              @if (firstGroupError) {
                <p class="modal-warning-text" role="alert">{{ firstGroupError }}</p>
              }
            </div>
            <footer class="modal-footer">
              <button type="button" class="btn-secondary" [disabled]="creating" (click)="createPersonalBasket()">
                רשימה אישית בלי קבוצה
              </button>
              <button
                type="button"
                class="btn-primary"
                [disabled]="creating || !firstGroupName.trim()"
                (click)="createBasketWithFirstGroup()"
                data-testid="first-group-confirm-button"
              >
                {{ creating ? 'יוצר…' : 'צור קבוצה ורשימה' }}
              </button>
            </footer>
          </div>
        </div>
      }

      <!-- Delete Confirmation Modal -->
      @if (basketToDelete) {
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title" (click)="cancelDelete()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2 id="delete-dialog-title">מחיקת רשימה</h2>
              <button type="button" class="modal-close-btn" (click)="cancelDelete()" aria-label="סגירה">×</button>
            </header>
            <div class="modal-body">
              <p>האם למחוק את הרשימה <strong>{{ basketToDelete.name || 'ללא שם' }}</strong> לצמיתות?</p>
              @if (basketToDelete.groupId) {
                <p class="modal-warning-text">שים לב: זוהי רשימה משותפת. המחיקה תמחק את הרשימה עבור כל חברי הקבוצה.</p>
              }
            </div>
            <footer class="modal-footer">
              <button type="button" class="btn-secondary" (click)="cancelDelete()">ביטול</button>
              <button
                type="button"
                class="btn-danger confirm-delete-btn"
                [disabled]="actionPendingId === basketToDelete.id"
                (click)="confirmDelete()"
              >
                {{ actionPendingId === basketToDelete.id ? 'מוחק…' : 'אישור מחיקה' }}
              </button>
            </footer>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .baskets-page {
      max-width: 1040px;
      margin: 0 auto;
      padding: 1.5rem 1rem 4rem;
      direction: rtl;
    }

    .baskets-hero {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1.25rem;
      margin-bottom: 2rem;
      padding-bottom: 1.25rem;
      border-bottom: 1px solid #e2e8f0;
      flex-wrap: wrap;
    }

    .hero-title-group h1 {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 700;
      color: #0f172a;
    }

    .btn-primary, .btn-secondary, .btn-danger {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      height: 40px;
      padding: 0 1.15rem;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
      text-decoration: none;
      white-space: nowrap;
    }

    .btn-primary {
      background: #1f6435;
      color: #ffffff;
      border: 1px solid #1f6435;
    }
    .btn-primary:hover:not(:disabled) {
      background: #184e29;
      border-color: #184e29;
    }

    .btn-secondary {
      background: #f8fafc;
      color: #334155;
      border: 1px solid #cbd5e1;
    }
    .btn-secondary:hover:not(:disabled) {
      background: #e2e8f0;
    }

    .btn-danger {
      background: #fff;
      color: #dc2626;
      border: 1px solid #fecaca;
    }
    .btn-danger:hover:not(:disabled) {
      background: #fef2f2;
      border-color: #f87171;
    }

    button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .baskets-section {
      margin-bottom: 2.5rem;
    }

    .section-title-wrap {
      margin-bottom: 1rem;
    }
    .section-title-wrap h2 {
      margin: 0 0 0.25rem;
      font-size: 1.25rem;
      font-weight: 700;
      color: #1e293b;
    }
    .section-hint {
      margin: 0;
      font-size: 0.85rem;
      color: #64748b;
    }

    .baskets-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
      gap: 1.25rem;
    }

    .basket-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 1.25rem;
      display: flex;
      flex-direction: column;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .basket-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .basket-card.is-current {
      border: 2px solid #1f6435;
      background: #fcfdfc;
    }

    .basket-card--closed {
      background: #f8fafc;
      border-color: #cbd5e1;
      opacity: 0.9;
    }

    .basket-card-header {
      margin-bottom: 0.75rem;
    }

    .basket-card-title-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      flex-wrap: wrap;
    }

    .basket-title-row {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }

    .basket-title {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 700;
      color: #0f172a;
      cursor: pointer;
      border-radius: 4px;
      padding: 0.1rem 0.25rem;
      margin-inline-start: -0.25rem;
      transition: background-color 0.15s ease;
    }

    .basket-title:hover {
      background-color: #f1f5f9;
    }

    .btn-rename-icon {
      background: transparent;
      border: none;
      cursor: pointer;
      font-size: 0.75rem;
      opacity: 0.6;
      padding: 0.15rem;
      border-radius: 4px;
      transition: opacity 0.15s ease, background-color 0.15s ease;
    }

    .btn-rename-icon:hover {
      opacity: 1;
      background-color: #f1f5f9;
    }

    .basket-rename-edit-row {
      display: flex;
      align-items: center;
      gap: 0.3rem;
    }

    .basket-rename-input {
      font-size: 0.95rem;
      font-weight: 600;
      padding: 0.2rem 0.4rem;
      border: 1.5px solid #2563eb;
      border-radius: 6px;
      outline: none;
      max-width: 13rem;
    }

    .btn-save-rename,
    .btn-cancel-rename {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.6rem;
      height: 1.6rem;
      border-radius: 6px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      border: 1px solid transparent;
    }

    .btn-save-rename {
      background: #16a34a;
      color: #fff;
    }

    .btn-cancel-rename {
      background: #f1f5f9;
      color: #475569;
      border-color: #cbd5e1;
    }

    .badge-group {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
    }

    .badge {
      display: inline-block;
      padding: 0.2rem 0.55rem;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .badge-shared {
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
    }
    .badge-personal {
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
    }
    .badge-open {
      background: #f0fdf4;
      color: #166534;
      border: 1px solid #bbf7d0;
    }
    .badge-closed {
      background: #f3f4f6;
      color: #6b7280;
      border: 1px solid #e5e7eb;
    }

    .basket-card-body {
      flex: 1;
      margin-bottom: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      font-size: 0.88rem;
    }

    .meta-row {
      display: flex;
      justify-content: space-between;
      color: #475569;
    }

    .basket-card-footer {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      border-top: 1px solid #f1f5f9;
      padding-top: 0.85rem;
    }
    .basket-card-footer .select-btn,
    .basket-card-footer .reopen-btn {
      flex: 1;
    }

    .empty-baskets-card {
      background: #ffffff;
      border: 1px dashed #cbd5e1;
      border-radius: 12px;
      padding: 2.5rem 1.5rem;
      text-align: center;
      color: #64748b;
    }

    .guest-warning {
      background: #fffbeb;
      border: 1px solid #fef3c7;
      border-radius: 12px;
      padding: 1.5rem;
      display: flex;
      gap: 1rem;
      align-items: center;
      color: #92400e;
    }
    .guest-warning h2 {
      margin: 0 0 0.25rem;
      font-size: 1.1rem;
    }
    .guest-warning p {
      margin: 0;
      font-size: 0.9rem;
    }

    /* Modal */
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
      z-index: 9999;
    }
    .modal-card {
      background: #ffffff;
      border-radius: 12px;
      max-width: 440px;
      width: 100%;
      padding: 1.5rem;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
      direction: rtl;
    }
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }
    .modal-header h2 {
      margin: 0;
      font-size: 1.2rem;
      color: #0f172a;
    }
    .modal-close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      color: #64748b;
      cursor: pointer;
      padding: 0;
    }
    .modal-body {
      margin-bottom: 1.5rem;
      color: #334155;
      font-size: 0.95rem;
    }
    .modal-warning-text {
      color: #b45309;
      font-size: 0.85rem;
      margin-top: 0.5rem;
    }
    .first-group-input {
      width: 100%;
      margin-top: 0.85rem;
      box-sizing: border-box;
      padding: 0.65rem 0.75rem;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      color: #0f172a;
    }
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
    }

    .list-rows {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .list-row {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      min-height: 48px;
      padding: 0.35rem 0;
      border-bottom: 1px solid #e2e8f0;
    }
    .list-name {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      border: 0;
      background: transparent;
      padding: 0;
      color: #0f172a;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
      text-align: right;
    }
    .list-name:disabled {
      cursor: default;
    }
    .list-name.is-open {
      overflow: visible;
    }
    .list-name.is-open .list-name-label {
      display: inline-block;
      max-width: 100%;
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: #dcfce7;
      color: #14532d;
      border-radius: 8px;
      padding: 0.15rem 0.4rem;
      margin-inline-start: -0.4rem;
    }
    .list-count {
      color: #64748b;
      font-size: 0.9rem;
      white-space: nowrap;
    }
    .list-delete {
      margin-inline-start: auto;
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #64748b;
      cursor: pointer;
    }
    .list-delete:hover:not(:disabled) {
      background: #fef2f2;
      color: #dc2626;
    }
    @media (max-width: 860px) {
      .baskets-page {
        padding: 1rem 0.75rem calc(4.75rem + env(safe-area-inset-bottom));
      }
      .baskets-hero {
        flex-direction: column;
        align-items: stretch;
      }
      .baskets-grid {
        grid-template-columns: 1fr;
      }
      .basket-card-footer {
        flex-direction: column;
      }
      .basket-card-footer button {
        width: 100%;
      }
      .basket-card-footer .select-btn,
      .basket-card-footer .reopen-btn {
        flex: none;
      }
      .basket-card-footer .select-btn {
        height: 52px;
        font-size: 1rem;
      }
    }
  `]
})
export class BasketsManagerComponent implements OnInit {
  readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly localProfile = inject(LocalProfileService);
  private readonly router = inject(Router);

  baskets: BasketSummary[] = [];
  loading = false;
  creating = false;
  errorMessage = '';
  actionPendingId: number | null = null;
  basketToDelete: BasketSummary | null = null;
  askFirstGroup = false;
  firstGroupName = '';
  firstGroupError = '';

  get openBaskets(): BasketSummary[] {
    return this.baskets.filter((b) => !b.closedAt);
  }

  get closedBaskets(): BasketSummary[] {
    return this.baskets.filter((b) => !!b.closedAt);
  }

  ngOnInit(): void {
    if (this.auth.isLoggedIn) {
      this.loadBaskets();
    } else {
      this.auth.currentUser$.subscribe((user) => {
        if (user) {
          this.loadBaskets();
        }
      });
    }
  }

  loadBaskets(): void {
    this.loading = true;
    this.errorMessage = '';
    this.auth.getUserBaskets().subscribe({
      next: (list) => {
        this.baskets = list;
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'אירעה שגיאה בטעינת הרשימות.';
        this.loading = false;
      }
    });
  }

  isOpenList(basketId: number): boolean {
    return this.localProfile.getStoreListBasketId() === basketId;
  }

  openList(basket: BasketSummary): void {
    this.actionPendingId = basket.id;
    const go = () => {
      this.localProfile.setStoreListBasketId(basket.id);
      this.actionPendingId = null;
      void this.router.navigate(['/']);
    };
    if (!basket.closedAt) {
      go();
      return;
    }
    this.auth.openBasket(basket.id).subscribe({
      next: () => go(),
      error: () => {
        this.actionPendingId = null;
        this.errorMessage = 'אירעה שגיאה בפתיחת הרשימה.';
      }
    });
  }

  createNewBasket(): void {
    if (!this.auth.isLoggedIn) {
      this.auth.openLoginModal();
      return;
    }

    this.auth.getGroups().subscribe({
      next: (groups) => {
        if (groups.length === 0) {
          this.askFirstGroup = true;
          this.firstGroupName = '';
          this.firstGroupError = '';
          return;
        }
        this.createBasketNow();
      },
      error: () => {
        this.errorMessage = 'אירעה שגיאה בטעינת הקבוצות.';
      }
    });
  }

  cancelFirstGroup(): void {
    if (this.creating) return;
    this.askFirstGroup = false;
    this.firstGroupError = '';
  }

  createPersonalBasket(): void {
    this.askFirstGroup = false;
    this.createBasketNow(null);
  }

  createBasketWithFirstGroup(): void {
    const name = this.firstGroupName.trim();
    if (!name) return;
    this.creating = true;
    this.firstGroupError = '';
    this.auth.createGroup(name, true).subscribe({
      next: (group) => {
        this.askFirstGroup = false;
        this.createBasketNow(group.id);
      },
      error: () => {
        this.creating = false;
        this.firstGroupError = 'אירעה שגיאה ביצירת הקבוצה.';
      }
    });
  }

  private nextListName(): string {
    const base = 'רשימה לסופר';
    const taken = new Set(this.openBaskets.map((basket) => (basket.name || '').trim()));
    if (!taken.has(base)) return base;
    let index = 2;
    while (taken.has(`${base} ${index}`)) index += 1;
    return `${base} ${index}`;
  }

  private createBasketNow(groupId?: number | null): void {
    this.creating = true;
    const useDefaultGroup = groupId === undefined;
    this.api.createShoppingBasket(this.nextListName(), groupId ?? null, null, {
      makeCurrent: false,
      useDefaultGroup
    }).subscribe({
      next: (basket) => {
        this.creating = false;
        this.localProfile.setStoreListBasketId(basket.id);
        void this.router.navigate(['/']);
      },
      error: () => {
        this.creating = false;
        this.errorMessage = 'אירעה שגיאה ביצירת הרשימה.';
      }
    });
  }

  deleteBasket(basket: BasketSummary): void {
    this.basketToDelete = basket;
  }

  cancelDelete(): void {
    this.basketToDelete = null;
  }

  confirmDelete(): void {
    if (!this.basketToDelete) return;
    const id = this.basketToDelete.id;
    this.actionPendingId = id;
    this.auth.deleteBasket(id).subscribe({
      next: () => {
        if (this.localProfile.getStoreListBasketId() === id) {
          this.localProfile.clearStoreListBasketId();
        }
        this.actionPendingId = null;
        this.basketToDelete = null;
        this.loadBaskets();
      },
      error: () => {
        this.actionPendingId = null;
      }
    });
  }
}
